import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { http } from "../utils/config";

const DEFAULTS = {
  title: "Bota Connect",
  description: "Kết nối thương mại điện tử Bota Web",
  keywords: "",
  favicon: "",
  og_image: "",
};

/**
 * Resolve a relative backend path like "/seo/favicon-123.png"
 * to an absolute URL based on the API base (minus /api suffix).
 */
function resolveBackendUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  // API base looks like "http://127.0.0.1:8000/api" — strip "/api"
  const base = (http.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return base + path;
}

const SeoContext = createContext({
  seo: DEFAULTS,
  pages: {},
  refreshSeo: () => {},
});

export function SeoProvider({ children }) {
  const [seo, setSeo] = useState(DEFAULTS);
  const [pages, setPages] = useState({});

  const fetchSeo = useCallback(async () => {
    try {
      const res = await http.get("/site-settings/seo");
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setSeo({
          title: d.title || DEFAULTS.title,
          description: d.description || DEFAULTS.description,
          keywords: d.keywords || DEFAULTS.keywords,
          favicon: d.favicon || "",
          og_image: d.og_image || "",
        });
        setPages(d.pages || {});
      }
    } catch {
      // Silent fallback — SEO fetch fail should not block app
    }
  }, []);

  useEffect(() => {
    fetchSeo();
  }, [fetchSeo]);

  return (
    <SeoContext.Provider value={{ seo, pages, refreshSeo: fetchSeo }}>
      {children}
    </SeoContext.Provider>
  );
}

/**
 * SeoHead — must be rendered inside a Router.
 * Applies per-page SEO (or global fallback) + favicon + OG tags to document head.
 */
export function SeoHead() {
  const { seo, pages } = useContext(SeoContext);
  const location = useLocation();

  // Apply title / description / keywords per route
  useEffect(() => {
    const pageSeo = pages[location.pathname];
    const title = pageSeo?.title || seo.title;
    const description = pageSeo?.description || seo.description;
    const keywords = pageSeo?.keywords || seo.keywords;

    document.title = title;

    setOrCreateMeta("description", description);
    if (keywords) {
      setOrCreateMeta("keywords", keywords);
    } else {
      removeMeta("keywords");
    }
  }, [seo, pages, location.pathname]);

  // Apply favicon
  useEffect(() => {
    if (!seo.favicon) return;
    const url = resolveBackendUrl(seo.favicon);
    const link =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]');
    if (link) {
      link.setAttribute("href", url);
    }
    // Also update apple-touch-icon
    const apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (apple) {
      apple.setAttribute("href", url);
    }
  }, [seo.favicon]);

  // Apply Open Graph / Twitter meta tags
  useEffect(() => {
    const pageSeo = pages[location.pathname];
    const title = pageSeo?.title || seo.title;
    const description = pageSeo?.description || seo.description;
    const imageUrl = seo.og_image ? resolveBackendUrl(seo.og_image) : "";

    setOrCreateMetaProperty("og:title", title);
    setOrCreateMetaProperty("og:description", description);
    setOrCreateMetaProperty("og:type", "website");
    setOrCreateMeta("twitter:card", imageUrl ? "summary_large_image" : "summary");
    setOrCreateMeta("twitter:title", title);
    setOrCreateMeta("twitter:description", description);

    if (imageUrl) {
      setOrCreateMetaProperty("og:image", imageUrl);
      setOrCreateMeta("twitter:image", imageUrl);
    } else {
      removeMetaProperty("og:image");
      removeMeta("twitter:image");
    }
  }, [seo, pages, location.pathname]);

  return null;
}

/* ── DOM helpers ── */

function setOrCreateMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.remove();
}

function setOrCreateMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMetaProperty(property) {
  const el = document.querySelector(`meta[property="${property}"]`);
  if (el) el.remove();
}

export function useSeo() {
  return useContext(SeoContext);
}
